import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Loader2, RefreshCw, Plus, Search, ArrowLeft, Save, Trash2, Eye, EyeOff,
  ExternalLink, Calendar, FileText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function slugify(s) {
  return (s || "").toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

/* -------- List view -------- */
function InsightsList({ posts, loading, onRefresh, onNew, onEdit, onDelete, onToggleStatus }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts.filter((p) => {
      if (status !== "all" && p.status !== status) return false;
      if (!needle) return true;
      return [p.title, p.tag, p.excerpt, p.slug].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [posts, q, status]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between mb-8 gap-4">
        <div>
          <div className="eyebrow text-brand-orange mb-2">Admin</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-brand-blue leading-tight">Insights CMS.</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Publish, edit and unpublish thought-leadership posts. Drafts stay private until you publish them.</p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-insights-new-btn" onClick={onNew} className="btn-primary whitespace-nowrap"><Plus className="w-4 h-4" /> New post</button>
          <button data-testid="admin-insights-refresh-btn" onClick={onRefresh} className="btn-secondary whitespace-nowrap"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-insights-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, tag, slug…" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:outline-none focus:border-brand-blue" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Status</label>
          <select data-testid="admin-insights-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue">
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div data-testid="admin-insights-counter" className="text-xs uppercase tracking-widest text-slate-500 font-semibold ml-auto">{filtered.length} of {posts.length}</div>
      </div>

      {loading ? (
        <div className="py-24 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading posts…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 p-16 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500 text-sm mb-4">{posts.length === 0 ? "No posts yet." : "No posts match your filters."}</div>
          {posts.length === 0 && <button data-testid="admin-insights-empty-new-btn" onClick={onNew} className="btn-primary"><Plus className="w-4 h-4" /> Write your first post</button>}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Tag</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right min-w-[250px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`admin-insight-row-${p.slug}`} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{p.title}</div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mt-0.5">/insights/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{p.tag}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1" />{fmtDate(p.date)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border ${p.status === "published" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
                      {p.status === "published" ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {p.status === "published" && (
                      <a data-testid={`admin-insight-view-${p.slug}`} href={`/insights/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest font-semibold text-slate-600 hover:text-brand-orange inline-flex items-center gap-1 mr-4">
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    )}
                    <button data-testid={`admin-insight-toggle-${p.slug}`} onClick={() => onToggleStatus(p)} className="text-xs uppercase tracking-widest font-semibold text-slate-600 hover:text-brand-orange mr-4">
                      {p.status === "published" ? "Unpublish" : "Publish"}
                    </button>
                    <button data-testid={`admin-insight-edit-${p.slug}`} onClick={() => onEdit(p)} className="text-xs uppercase tracking-widest font-semibold text-brand-orange hover:text-brand-blue mr-4">Edit</button>
                    <button data-testid={`admin-insight-delete-${p.slug}`} onClick={() => onDelete(p)} className="text-xs uppercase tracking-widest font-semibold text-red-700 hover:text-red-900 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------- Editor -------- */
const EMPTY_POST = {
  title: "", slug: "", tag: "", date: new Date().toISOString().slice(0, 10),
  excerpt: "", body: "", cover_image_url: "", source_url: "", status: "draft",
  meta_description: "",
};

function InsightEditor({ initial, onBack, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState(initial ? { ...EMPTY_POST, ...initial } : EMPTY_POST);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isNew && !slugTouched) setField("slug", slugify(form.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const save = async (publishOverride) => {
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!form.slug.trim()) return toast.error("Slug is required.");
    if (!form.excerpt.trim()) return toast.error("Excerpt is required.");
    if (!form.tag.trim()) return toast.error("Tag is required.");
    const payload = {
      ...form,
      slug: slugify(form.slug),
      status: publishOverride || form.status,
    };
    setBusy(true);
    try {
      const { data } = isNew
        ? await axios.post(`${API}/admin/blog/posts`, payload)
        : await axios.put(`${API}/admin/blog/posts/${initial.id}`, payload);
      toast.success(isNew ? "Post created" : "Post saved");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <button data-testid="admin-insight-editor-back" onClick={onBack} className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-brand-orange inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to list
      </button>
      <div className="flex flex-wrap items-start justify-between mb-6 gap-4">
        <div>
          <div className="eyebrow text-brand-orange mb-2">{isNew ? "New insight" : "Editing"}</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-brand-blue">{isNew ? "Write a new post" : form.title || "Untitled"}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-insight-editor-preview-toggle" onClick={() => setPreview((v) => !v)} className="btn-secondary text-xs">
            {preview ? <><EyeOff className="w-4 h-4" /> Edit</> : <><Eye className="w-4 h-4" /> Preview</>}
          </button>
          <button data-testid="admin-insight-editor-save-draft" onClick={() => save("draft")} disabled={busy} className="btn-secondary text-xs disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
          </button>
          <button data-testid="admin-insight-editor-publish" onClick={() => save("published")} disabled={busy} className="btn-primary text-xs disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} {form.status === "published" ? "Update" : "Publish"}
          </button>
        </div>
      </div>

      {preview ? (
        <article className="bg-white border border-slate-200 p-8 lg:p-12 max-w-[820px] mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-orange mb-2">{form.tag}</div>
          <h1 className="font-serif text-3xl text-brand-blue leading-tight mb-3">{form.title || "Untitled"}</h1>
          <div className="text-xs text-slate-500 mb-6">{fmtDate(form.date)} · By Andrea Kanaris</div>
          {form.cover_image_url && <img src={form.cover_image_url} alt="" className="w-full h-auto mb-6 border border-slate-200" />}
          <p className="text-lg text-slate-700 font-serif italic border-b border-slate-200 pb-6 mb-6">{form.excerpt}</p>
          <div className="insight-prose">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body || "*(No body yet)*"}</ReactMarkdown>
          </div>
        </article>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Title *">
              <input data-testid="admin-insight-field-title" value={form.title} onChange={(e) => setField("title", e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 text-base focus:outline-none focus:border-brand-blue" placeholder="A crisp, click-worthy headline" />
            </Field>
            <Field label="Excerpt *" hint="1-2 sentences shown on the listing page and in social share cards.">
              <textarea data-testid="admin-insight-field-excerpt" value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} rows={3} className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-blue" placeholder="Short summary…" />
            </Field>
            <Field label="Body (Markdown)" hint="Supports GFM markdown: # heading, **bold**, *italic*, - lists, [links](url).">
              <textarea data-testid="admin-insight-field-body" value={form.body} onChange={(e) => setField("body", e.target.value)} rows={22} className="w-full border border-slate-300 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-brand-blue" placeholder="Write your post…" />
            </Field>
          </div>
          <div className="space-y-4">
            <Field label="Slug *" hint="Auto-generated from the title; edit for a cleaner URL.">
              <input data-testid="admin-insight-field-slug" value={form.slug} onChange={(e) => { setField("slug", e.target.value); setSlugTouched(true); }} className="w-full border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue" placeholder="my-post-slug" />
              <div className="text-[11px] text-slate-500 mt-1">/insights/<span className="font-semibold text-brand-blue">{slugify(form.slug) || "your-slug"}</span></div>
            </Field>
            <Field label="Tag *">
              <input data-testid="admin-insight-field-tag" value={form.tag} onChange={(e) => setField("tag", e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" placeholder="e.g. ESG, Housing, SIA" />
            </Field>
            <Field label="Published date">
              <input data-testid="admin-insight-field-date" type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" />
            </Field>
            <Field label="Cover image URL" hint="Optional. Used at the top of the post and in OG social cards.">
              <input data-testid="admin-insight-field-cover" value={form.cover_image_url} onChange={(e) => setField("cover_image_url", e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" placeholder="https://…" />
            </Field>
            <Field label="Source URL" hint="Optional link to the original article on WordPress or elsewhere.">
              <input data-testid="admin-insight-field-source" value={form.source_url} onChange={(e) => setField("source_url", e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" placeholder="https://…" />
            </Field>
            <Field label="SEO meta description" hint="Overrides the excerpt on search results. Keep under ~160 chars.">
              <textarea data-testid="admin-insight-field-meta" value={form.meta_description} onChange={(e) => setField("meta_description", e.target.value)} rows={3} className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-brand-blue" placeholder="Optional SEO meta description" />
            </Field>
            <div className="bg-slate-50 border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Status</div>
              <div className="text-sm text-slate-800">{form.status === "published" ? "Currently published" : "Currently a draft"}</div>
              <div className="text-xs text-slate-500 mt-1">Use the buttons at the top to save as draft or publish.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{hint}</div>}
    </div>
  );
}

/* -------- Container -------- */
export default function AdminInsights() {
  const { user, status: authStatus } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, {} = new, {id,...} = edit

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/blog/posts`)
      .then(({ data }) => setPosts(data.posts || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load posts."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authStatus === "authed" && user?.role === "admin") load();
  }, [authStatus, user]);

  if (authStatus === "checking") return <div className="min-h-[60vh] flex items-center justify-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking session…</div>;
  if (authStatus !== "authed") return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/member/hub" replace />;

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${p.id}`);
      toast.success("Post deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const onToggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/admin/blog/posts/${p.id}`, { status: next });
      toast.success(next === "published" ? "Published" : "Moved to draft");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed."); }
  };

  return (
    <div data-testid="admin-insights-page" className="bg-slate-50 min-h-screen py-14 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        {editing === null ? (
          <InsightsList
            posts={posts}
            loading={loading}
            onRefresh={load}
            onNew={() => setEditing({})}
            onEdit={(p) => setEditing(p)}
            onDelete={onDelete}
            onToggleStatus={onToggleStatus}
          />
        ) : (
          <InsightEditor
            initial={editing.id ? editing : null}
            onBack={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}
